import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const tokenRef = useRef(token);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const getHeaders = useCallback(() => {
    const t = tokenRef.current || localStorage.getItem('auth_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    const t = tokenRef.current || localStorage.getItem('auth_token');
    if (!t) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setUser(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Auth check failed:', err.message);
      setUser(null);
      localStorage.removeItem('auth_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        setToken(data.token);
        tokenRef.current = data.token;
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getHeaders() });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Logout request failed:', err.message);
    }
    setUser(null);
    setToken(null);
    tokenRef.current = null;
    localStorage.removeItem('auth_token');
  };

  const api = useCallback((method, url, data = null, config = {}) => {
    const cfg = {
      method,
      url: `${API}${url}`,
      headers: { ...getHeaders(), ...config.headers },
      ...config
    };
    if (data) cfg.data = data;
    return axios(cfg);
  }, [getHeaders]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, api, token, getHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}
